import React, { useState, useCallback } from 'react';
import './Revenue.css';
import AdminLoading from './AdminLoading';
import useRevenue from '../hooks/useRevenue';
import AddAccountModal from './AddAccountModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import DepositWithdrawalModal from './DepositWithdrawalModal';
import TransactionDetailModal from './TransactionDetailModal';
import PromoCodesPanel from './PromoCodesPanel';
import FreeDeliveryPromoSettings from './FreeDeliveryPromoSettings';
import { pickVisibleAccountNumber } from '../../utils/paymentAccountDisplay';
import {
  buildRevenueOverviewForRange,
  getRevenueGrossIncome,
  toLocalDateString,
} from '../utils/orderRevenueTransactions';
import { hasAccess } from '../constants/teamAccessPermissions';

function getAccountDetailsObject(account) {
  let det = account?.accountDetails;
  if (typeof det === 'string') {
    try {
      det = JSON.parse(det);
    } catch {
      det = {};
    }
  }
  if (!det || typeof det !== 'object') det = {};
  return det;
}

const Revenue = ({ currentUser }) => {
  const isCreatorScoped = currentUser?.role === 'team';
  const creatorScopeId = isCreatorScoped
    ? String(currentUser?._id || currentUser?.id || '').trim()
    : '';
  const canManageStoreRevenue =
    !isCreatorScoped && hasAccess('Revenue & Accounts|Manage', currentUser);

  const { 
    revenueData, 
    loading, 
    error, 
    refetch,
    refetchStats,
    refetchOverview,
    refetchTransactions,
    refetchCODIncome,
    addPaymentAccount,
    updatePaymentAccount,
    deletePaymentAccount,
    toggleAccountVisibility,
    toggleAccountStatus,
    addRevenueTransaction,
    updateRevenueTransaction,
    deleteRevenueTransaction
  } = useRevenue({ creatorScopeId });
  
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'transactions', 'accounts'
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, accountId: null, accountName: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [depositWithdrawalModal, setDepositWithdrawalModal] = useState({ isOpen: false, account: null });
  const [viewTransactionModal, setViewTransactionModal] = useState({ isOpen: false, transaction: null });
  const [editTransactionModal, setEditTransactionModal] = useState({ isOpen: false, transaction: null });
  const [deleteTransactionConfirm, setDeleteTransactionConfirm] = useState({ isOpen: false, transactionId: null, transactionInfo: '' });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    preset: 'all' // 'all', 'daily', 'weekly', 'monthly', 'yearly'
  });

  const breakdownOverview = React.useMemo(
    () =>
      buildRevenueOverviewForRange(
        revenueData.transactions || [],
        dateRange.startDate,
        dateRange.endDate
      ),
    [revenueData.transactions, dateRange.startDate, dateRange.endDate]
  );

  // Calculate investment balance from transactions
  const investmentBalance = React.useMemo(() => {
    if (!revenueData.transactions) return 0;
    
    return revenueData.transactions
      .filter(t => {
        const source = (t.source || '').toLowerCase();
        return source.includes('investment') || 
               (t.accountName && t.accountName.toLowerCase().includes('investment')) ||
               (t.accountId === 'investment');
      })
      .reduce((sum, t) => {
        if (t.type === 'deposit' || t.type === 'income') {
          return sum + (t.amount || 0);
        } else if (t.type === 'withdrawal' || t.type === 'expense') {
          return sum - (t.amount || 0);
        }
        return sum;
      }, 0);
  }, [revenueData.transactions]);

  // Get investment accounts from accounts list
  const investmentAccounts = React.useMemo(() => {
    if (!revenueData.accounts) return [];
    return revenueData.accounts.filter(acc => 
      (acc.paymentMethod || '').toLowerCase() === 'investment'
    );
  }, [revenueData.accounts]);

  const handleRefresh = () => {
    refetch();
  };

  const handlePromoTabNotify = useCallback(({ type, text }) => {
    if (type === 'success') {
      setErrorMessage('');
      setSuccessMessage(text);
      setTimeout(() => setSuccessMessage(''), 4000);
    } else {
      setSuccessMessage('');
      setErrorMessage(text);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  }, []);

  const formatCurrency = (amount) => {
    return `PKR ${amount.toLocaleString()}`;
  };

  // Helper function to get week number from date
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Helper function to get date from year and week number
  const getDateFromWeek = (year, week) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    return ISOweekStart;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#28a745',
      pending: '#ffc107',
      processing: '#17a2b8',
      cancelled: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  if (loading) {
    return (
      <AdminLoading
        title="Loading Revenue"
        subtitle="Fetching financial data..."
        icon="💰"
      />
    );
  }

  if (error) {
    return (
      <div className="revenue-error">
        <div className="error-content">
          <h3>⚠️ Error Loading Revenue Data</h3>
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            <i className="fas fa-refresh"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="revenue">
      <div className="revenue-header">
        <div className="header-content">
          <h1>{isCreatorScoped ? 'My revenue' : 'Revenue & Accounts'}</h1>
          <p>
            {isCreatorScoped
              ? 'Income from your product line items on confirmed orders (same rules as Sales). Store payment accounts are admin-only.'
              : 'Financial overview and transaction history'}
          </p>
        </div>
        <div className="header-actions">
          <button onClick={handleRefresh} className="refresh-btn">
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="revenue-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-chart-line"></i> Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <i className="fas fa-exchange-alt"></i> Transactions
        </button>
        {!isCreatorScoped && (
          <button 
            className={`tab-btn ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounts')}
          >
            <i className="fas fa-wallet"></i> Payment Accounts
          </button>
        )}
        {!isCreatorScoped && (
          <button
            className={`tab-btn ${activeTab === 'promos' ? 'active' : ''}`}
            onClick={() => setActiveTab('promos')}
          >
            <i className="fas fa-ticket-alt"></i> Promo codes
          </button>
        )}
      </div>

      {/* Revenue Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {isCreatorScoped && (
            <p className="revenue-scope-note" style={{ margin: '0 0 1rem', opacity: 0.85 }}>
              Amounts are your share per order (line totals only). For product and payment tables, open{' '}
              <strong>Sales</strong> in the sidebar.
            </p>
          )}
          <div className="revenue-overview">
            <div className="revenue-grid-compact">
              {!isCreatorScoped && (
              <>
              {/* Individual Account Cards (excluding investment accounts) */}
              {revenueData.accounts && revenueData.accounts.length > 0 && revenueData.accounts
                .filter(account => (account.paymentMethod || '').toLowerCase() !== 'investment')
                .map(account => (
                <div 
                  key={account._id || account.id} 
                  className="revenue-card-compact account-card clickable-card"
                  onClick={() => {
                    // Calculate current balance from transactions
                    const accountIdStr = String(account._id || account.id || '').trim();
                    const accountNameStr = String(account.name || '').toLowerCase().trim();
                    
                    const accountBalance = revenueData.transactions
                      ? revenueData.transactions
                          .filter(t => {
                            // Get transaction account identifiers (check multiple possible fields)
                            const tPaymentAccountId = t.paymentAccount?._id || t.paymentAccount || null;
                            const tPaymentAccountName = t.paymentAccount?.name ? String(t.paymentAccount.name).toLowerCase().trim() : null;
                            const tAccountId = String(t.accountId || '').trim();
                            const tAccountName = String(t.accountName || '').toLowerCase().trim();
                            
                            // Primary match: paymentAccount._id (new structure - most reliable)
                            if (tPaymentAccountId && accountIdStr && String(tPaymentAccountId).trim() === accountIdStr) {
                              return true;
                            }
                            
                            // Secondary match: paymentAccount.name (new structure)
                            if (tPaymentAccountName && accountNameStr && tPaymentAccountName === accountNameStr) {
                              return true;
                            }
                            
                            // Tertiary match: accountId (legacy field)
                            if (tAccountId && accountIdStr && tAccountId === accountIdStr) {
                              return true;
                            }
                            
                            // Fourth match: accountName (legacy field)
                            if (tAccountName && accountNameStr && tAccountName === accountNameStr) {
                              return true;
                            }
                            
                            // Fallback: match by source (for older transactions only)
                            if (t.source && String(t.source).toLowerCase().trim() === accountNameStr) {
                              return true;
                            }
                            
                            return false;
                          })
                          .filter(t => t.status === 'completed')
                          .reduce((sum, t) => {
                            const amount = parseFloat(t.amount) || 0;
                            if (t.type === 'income' || t.type === 'deposit') {
                              return sum + amount;
                            } else if (t.type === 'withdrawal' || t.type === 'expense') {
                              return sum - amount;
                            }
                            return sum;
                          }, 0)
                      : (account.totalIncome || account.stats?.totalAmount || 0);
                    
                    setDepositWithdrawalModal({ 
                      isOpen: true, 
                      account: { 
                        ...account, 
                        balance: Math.max(0, accountBalance)
                      } 
                    });
                  }}
                >
                  <div className="card-icon-wrapper">
                    <i className={`fas fa-${account.icon || 'wallet'}`}></i>
                  </div>
                  <div className="card-content-compact">
                    <div className="card-label">{account.name}</div>
                    <div className="card-amount">
                      {formatCurrency(
                        (() => {
                          // Always calculate balance from transactions for accurate display
                          // This ensures balance matches what's shown in deposit modal
                          if (revenueData.transactions && revenueData.transactions.length > 0) {
                            const accountIdStr = String(account._id || account.id || '').trim();
                            const accountNameStr = String(account.name || '').toLowerCase().trim();
                            
                            const accountTransactions = revenueData.transactions.filter(t => {
                              // Get transaction account identifiers (check multiple possible fields)
                              const tPaymentAccountId = t.paymentAccount?._id || t.paymentAccount || null;
                              const tPaymentAccountName = t.paymentAccount?.name ? String(t.paymentAccount.name).toLowerCase().trim() : null;
                              const tAccountId = String(t.accountId || '').trim();
                              const tAccountName = String(t.accountName || '').toLowerCase().trim();
                              
                              // Primary match: paymentAccount._id (new structure - most reliable)
                              if (tPaymentAccountId && accountIdStr && String(tPaymentAccountId).trim() === accountIdStr) {
                                return true;
                              }
                              
                              // Secondary match: paymentAccount.name (new structure)
                              if (tPaymentAccountName && accountNameStr && tPaymentAccountName === accountNameStr) {
                                return true;
                              }
                              
                              // Tertiary match: accountId (legacy field)
                              if (tAccountId && accountIdStr && tAccountId === accountIdStr) {
                                return true;
                              }
                              
                              // Fourth match: accountName (legacy field)
                              if (tAccountName && accountNameStr && tAccountName === accountNameStr) {
                                return true;
                              }
                              
                              // Fallback: match by source (for older transactions only)
                              if (t.source) {
                                const tSource = String(t.source).toLowerCase().trim();
                                if (tSource === accountNameStr) {
                                  return true;
                                }
                              }
                              
                              return false;
                            });
                            
                            const calculatedBalance = accountTransactions
                              .filter(t => t.status === 'completed')
                              .reduce((sum, t) => {
                                const amount = parseFloat(t.amount) || 0;
                                if (t.type === 'income' || t.type === 'deposit') {
                                  return sum + amount;
                                } else if (t.type === 'withdrawal' || t.type === 'expense') {
                                  return sum - amount;
                                }
                                return sum;
                              }, 0);
                            
                            // Debug: Log balance calculation for this account
                            if (account.name && (account.name.toLowerCase().includes('easypaisa') || account.name.toLowerCase().includes('super'))) {
                              console.log(`💰 Card Balance Calculation for "${account.name}":`, {
                                accountId: account._id || account.id,
                                accountTotalIncome: account.totalIncome,
                                matchedTransactions: accountTransactions.length,
                                calculatedBalance: calculatedBalance,
                                transactions: accountTransactions.map(t => ({
                                  id: t._id,
                                  type: t.type,
                                  amount: t.amount,
                                  paymentAccount: t.paymentAccount?._id || t.paymentAccount,
                                  paymentAccountName: t.paymentAccount?.name
                                }))
                              });
                            }
                            
                            // Return calculated balance (even if 0)
                            return Math.max(0, calculatedBalance);
                          }
                          
                          // Fallback: use account.totalIncome if transactions not available
                          if (account.totalIncome !== undefined && account.totalIncome !== null) {
                            return account.totalIncome;
                          }
                          
                          // Final fallback: show 0 if no data available
                          return account.stats?.totalAmount || 0;
                        })()
                      )}
                    </div>
                    <div className="card-subtitle">{account.paymentMethod || 'Payment Account'}</div>
                    <div className="card-action-hint">
                      <i className="fas fa-plus-circle"></i> Click to deposit/withdraw
                    </div>
                  </div>
                </div>
              ))}
              
              {/* COD Account Card */}
              {(revenueData.codRealIncome > 0 || revenueData.codPendingIncome > 0) && (
                <div 
                  className="revenue-card-compact account-card cod-card clickable-card"
                  onClick={() => setDepositWithdrawalModal({ 
                    isOpen: true, 
                    account: { 
                      name: 'Cash on Delivery',
                      source: 'Cash on Delivery',
                      icon: 'money-bill-wave',
                      balance: revenueData.overview?.codRealIncome || revenueData.codRealIncome || 0,
                      id: 'cod',
                      _id: 'cod'
                    } 
                  })}
                >
                  <div className="card-icon-wrapper">
                    <i className="fas fa-money-bill-wave"></i>
                  </div>
                  <div className="card-content-compact">
                    <div className="card-label">Cash on Delivery</div>
                    <div className="card-amount">
                      {formatCurrency(revenueData.overview?.codRealIncome || revenueData.codRealIncome || 0)}
                    </div>
                    <div className="card-subtitle">Delivered Orders</div>
                    {revenueData.codPendingIncome > 0 && (
                      <div className="card-pending-note">
                        Pending: {formatCurrency(revenueData.codPendingIncome || 0)}
                      </div>
                    )}
                    <div className="card-total-note">Real-Time Income</div>
                    <div className="card-action-hint">
                      <i className="fas fa-plus-circle"></i> Click to deposit/withdraw
                    </div>
                  </div>
                </div>
              )}

              {/* Investment Account Cards */}
              {investmentAccounts && investmentAccounts.length > 0 && investmentAccounts.map(investmentAccount => (
                <div 
                  key={investmentAccount._id || investmentAccount.id} 
                  className="revenue-card-compact account-card investment-card clickable-card"
                  onClick={() => {
                    // Calculate balance for this specific investment account
                    const accountBalance = revenueData.transactions
                      ? revenueData.transactions
                          .filter(t => 
                            (t.accountId === (investmentAccount._id || investmentAccount.id)) ||
                            (t.accountName === investmentAccount.name)
                          )
                          .reduce((sum, t) => {
                            if (t.type === 'deposit' || t.type === 'income') {
                              return sum + (t.amount || 0);
                            } else if (t.type === 'withdrawal' || t.type === 'expense') {
                              return sum - (t.amount || 0);
                            }
                            return sum;
                          }, investmentAccount.accountDetails?.investmentAmount || 0)
                      : (investmentAccount.accountDetails?.investmentAmount || 0);
                    
                    setDepositWithdrawalModal({ 
                      isOpen: true, 
                      account: { 
                        ...investmentAccount,
                        balance: accountBalance,
                        source: investmentAccount.name
                      } 
                    });
                  }}
                >
                  <div className="card-icon-wrapper">
                    <i className={`fas fa-${investmentAccount.icon || 'chart-line'}`}></i>
                  </div>
                  <div className="card-content-compact">
                    <div className="card-label">{investmentAccount.name}</div>
                    <div className="card-amount">
                      {formatCurrency(
                        revenueData.transactions
                          ? revenueData.transactions
                              .filter(t => 
                                (t.accountId === (investmentAccount._id || investmentAccount.id)) ||
                                (t.accountName === investmentAccount.name)
                              )
                              .reduce((sum, t) => {
                                if (t.type === 'deposit' || t.type === 'income') {
                                  return sum + (t.amount || 0);
                                } else if (t.type === 'withdrawal' || t.type === 'expense') {
                                  return sum - (t.amount || 0);
                                }
                                return sum;
                              }, investmentAccount.accountDetails?.investmentAmount || 0)
                          : (investmentAccount.accountDetails?.investmentAmount || 0)
                      )}
                    </div>
                    <div className="card-subtitle">
                      {investmentAccount.accountDetails?.investmentType || 'Investment Account'}
                      {investmentAccount.accountDetails?.interestRate && (
                        <span className="interest-rate"> • {investmentAccount.accountDetails.interestRate}%</span>
                      )}
                    </div>
                    {investmentAccount.accountDetails?.maturityDate && (
                      <div className="card-maturity-date">
                        Maturity: {new Date(investmentAccount.accountDetails.maturityDate).toLocaleDateString('en-PK', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    )}
                    <div className="card-action-hint">
                      <i className="fas fa-plus-circle"></i> Click to deposit/withdraw
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Fallback Investment Card (if no investment accounts exist) */}
              {(!investmentAccounts || investmentAccounts.length === 0) && (
                <div 
                  className="revenue-card-compact account-card investment-card clickable-card"
                  onClick={() => setDepositWithdrawalModal({ 
                    isOpen: true, 
                    account: { 
                      name: 'Investment',
                      source: 'Investment',
                      icon: 'chart-line',
                      balance: investmentBalance,
                      id: 'investment',
                      _id: 'investment',
                      paymentMethod: 'investment'
                    } 
                  })}
                >
                  <div className="card-icon-wrapper">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="card-content-compact">
                    <div className="card-label">Investment</div>
                    <div className="card-amount">
                      {formatCurrency(investmentBalance)}
                    </div>
                    <div className="card-subtitle">Investment Account</div>
                    <div className="card-action-hint">
                      <i className="fas fa-plus-circle"></i> Click to deposit/withdraw
                    </div>
                  </div>
                </div>
              )}
              </>
              )}

              {/* Total Income Card (Last Card) */}
              <div className="revenue-card-compact total-income">
                <div className="card-icon-wrapper">
                  <i className="fas fa-chart-pie"></i>
                </div>
                <div className="card-content-compact">
                  <div className="card-label">Total Income</div>
                  <div className="card-amount">
                    {formatCurrency(getRevenueGrossIncome(revenueData.overview))}
                  </div>
                  <div className="card-subtitle">Completed + pending (deduplicated)</div>
                  {revenueData.overview && (
                    <div className="card-accounts-list">
                      <div className="account-income-item">
                        <span className="account-name">Total Deposits</span>
                        <span className="account-income deposit-text">{formatCurrency(revenueData.overview.totalDeposits || 0)}</span>
                      </div>
                      <div className="account-income-item">
                        <span className="account-name">Total Withdrawals</span>
                        <span className="account-income withdrawal-text">{formatCurrency(revenueData.overview.totalWithdrawals || 0)}</span>
                      </div>
                    </div>
                  )}
                </div>
            </div>
            </div>
          </div>

          {/* Revenue Breakdown Cards */}
          {(revenueData.transactions?.length > 0 || revenueData.overview) && (
            <div className="revenue-breakdown">
              <div className="breakdown-header">
                <h2 className="section-title">Revenue Breakdown</h2>
                <div className="date-range-selector">
                  <div className="date-preset-buttons">
                    <button 
                      className={`preset-btn ${dateRange.preset === 'all' ? 'active' : ''}`}
                      onClick={() => setDateRange({ startDate: '', endDate: '', preset: 'all' })}
                    >
                      All Time
                    </button>
                    <button 
                      className={`preset-btn ${dateRange.preset === 'daily' ? 'active' : ''}`}
                      onClick={() => {
                        const today = toLocalDateString(new Date());
                        setDateRange({ startDate: today, endDate: today, preset: 'daily' });
                      }}
                    >
                      Daily
                    </button>
                    <button 
                      className={`preset-btn ${dateRange.preset === 'weekly' ? 'active' : ''}`}
                      onClick={() => {
                        const today = new Date();
                        const weekStart = new Date(today);
                        weekStart.setDate(today.getDate() - today.getDay());
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekStart.getDate() + 6);
                        setDateRange({
                          startDate: toLocalDateString(weekStart),
                          endDate: toLocalDateString(weekEnd),
                          preset: 'weekly',
                        });
                      }}
                    >
                      Weekly
                    </button>
                    <button 
                      className={`preset-btn ${dateRange.preset === 'monthly' ? 'active' : ''}`}
                      onClick={() => {
                        const today = new Date();
                        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                        setDateRange({
                          startDate: toLocalDateString(monthStart),
                          endDate: toLocalDateString(monthEnd),
                          preset: 'monthly',
                        });
                      }}
                    >
                      Monthly
                    </button>
                    <button 
                      className={`preset-btn ${dateRange.preset === 'yearly' ? 'active' : ''}`}
                      onClick={() => {
                        const today = new Date();
                        const yearStart = new Date(today.getFullYear(), 0, 1);
                        const yearEnd = new Date(today.getFullYear(), 11, 31);
                        setDateRange({
                          startDate: toLocalDateString(yearStart),
                          endDate: toLocalDateString(yearEnd),
                          preset: 'yearly',
                        });
                      }}
                    >
                      Yearly
                    </button>
                  </div>
                  <div className="date-inputs">
                    <div className="date-input-group">
                      <label>Start Date</label>
                      <input 
                        type="date" 
                        className="date-input"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value, preset: 'custom' }))}
                      />
                    </div>
                    <div className="date-input-group">
                      <label>End Date</label>
                      <input 
                        type="date" 
                        className="date-input"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value, preset: 'custom' }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="breakdown-grid-compact">
                {/* Combined: Total Income + Real Income */}
                <div className="breakdown-card-compact combined">
                  <div className="breakdown-icon income">
                    <i className="fas fa-wallet"></i>
                  </div>
                  <div className="breakdown-content">
                    <div className="breakdown-label">Total Income</div>
                    <div className="breakdown-value">
                      {formatCurrency(getRevenueGrossIncome(breakdownOverview))}
                    </div>
                    <div className="breakdown-subtitle">Completed + pending (each order once)</div>
                    <div className="breakdown-divider"></div>
                    <div className="breakdown-label">Real Income</div>
                    <div className="breakdown-value delivered-text">
                      {formatCurrency(breakdownOverview?.realIncome || breakdownOverview?.totalIncome || 0)}
                    </div>
                    <div className="breakdown-subtitle">Delivered Orders</div>
                  </div>
                </div>

                {/* Combined: Pending Income + COD Real Income */}
                <div className="breakdown-card-compact pending combined">
                  <div className="breakdown-icon pending-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="breakdown-content">
                    <div className="breakdown-label">Pending Income</div>
                    <div className="breakdown-value pending-text">
                      {formatCurrency(breakdownOverview?.pendingIncome || 0)}
                    </div>
                    <div className="breakdown-divider"></div>
                    <div className="breakdown-label">COD Real Income</div>
                    <div className="breakdown-value cod-text">
                      {formatCurrency(breakdownOverview?.codRealIncome || 0)}
                    </div>
                    <div className="breakdown-subtitle">Delivered COD</div>
                  </div>
                </div>

                {/* Combined: Total Deposits + Total Withdrawals */}
                <div className="breakdown-card-compact combined">
                  <div className="breakdown-icon deposit">
                    <i className="fas fa-exchange-alt"></i>
                  </div>
                  <div className="breakdown-content">
                    <div className="breakdown-label">Total Deposits</div>
                    <div className="breakdown-value deposit-text">
                      {formatCurrency(breakdownOverview.totalDeposits || 0)}
                    </div>
                    <div className="breakdown-divider"></div>
                    <div className="breakdown-label">Total Withdrawals</div>
                    <div className="breakdown-value withdrawal-text">
                      {formatCurrency(breakdownOverview.totalWithdrawals || 0)}
                    </div>
                  </div>
                </div>

                {/* Net Revenue */}
                <div className="breakdown-card-compact net">
                  <div className="breakdown-icon net-icon">
                    <i className="fas fa-chart-pie"></i>
                  </div>
                  <div className="breakdown-content">
                    <div className="breakdown-label">Net Revenue</div>
                    <div className="breakdown-value net-text">
                      {formatCurrency(breakdownOverview.netRevenue || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Revenue by Source */}
              {breakdownOverview.revenueBySource && breakdownOverview.revenueBySource.length > 0 && (
                <div className="revenue-by-source">
                  <h3>Revenue by Source</h3>
                  <div className="source-list">
                    {breakdownOverview.revenueBySource.map((item) => (
                      <div key={item._id} className="source-item">
                        <span className="source-name">{item._id}</span>
                        <span className="source-amount">{formatCurrency(item.total)}</span>
                        <span className="source-count">({item.count} transactions)</span>
                      </div>
                    ))}
                  </div>
          </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="transactions-section">
          <div className="transactions-header">
            <div className="transactions-header-left">
              <h2 className="transactions-title">
                <i className="fas fa-exchange-alt"></i>
                Revenue Transactions
              </h2>
              <p className="transactions-subtitle">
                {isCreatorScoped
                  ? 'Your product line income on confirmed orders (amounts are your share, not full order totals).'
                  : 'Manual entries plus confirmed sold orders · Team member shows who sold the products'}
              </p>
            </div>
            {!isCreatorScoped && canManageStoreRevenue && (
            <button className="add-transaction-btn" onClick={() => {/* TODO: Open add transaction modal */}}>
              <i className="fas fa-plus"></i>
              <span>Add Transaction</span>
            </button>
            )}
          </div>

          {/* Transactions Stats Cards */}
          {revenueData.transactions && revenueData.transactions.length > 0 && (
            <div className="transactions-stats">
              <div className="transaction-stat-card">
                <div className="stat-icon income-stat">
                  <i className="fas fa-arrow-down"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Income</div>
                  <div className="stat-value">
                    {formatCurrency(revenueData.overview?.totalIncome ?? 0)}
                  </div>
                </div>
              </div>
              <div className="transaction-stat-card">
                <div className="stat-icon expense-stat">
                  <i className="fas fa-arrow-up"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Expenses</div>
                  <div className="stat-value">
                    {formatCurrency(
                      revenueData.transactions
                        .filter(t => (t.type === 'withdrawal' || t.type === 'expense') && t.status === 'completed')
                        .reduce((sum, t) => sum + (t.amount || 0), 0)
                    )}
                  </div>
                </div>
              </div>
              <div className="transaction-stat-card">
                <div className="stat-icon pending-stat">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Pending</div>
                  <div className="stat-value">
                    {formatCurrency(
                      revenueData.transactions
                        .filter(t => t.status === 'pending')
                        .reduce((sum, t) => sum + (t.amount || 0), 0)
                    )}
            </div>
          </div>
        </div>
              <div className="transaction-stat-card">
                <div className="stat-icon total-stat">
                  <i className="fas fa-list"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Transactions</div>
                  <div className="stat-value">{revenueData.transactions.length}</div>
          </div>
        </div>
      </div>
          )}

          {/* Transactions Table */}
          <div className="transactions-table-wrapper">
        <div className="transactions-table">
              <div className="table-header-row">
            <div className="header-cell">Transaction ID</div>
                <div className="header-cell">Type</div>
                <div className="header-cell">Source</div>
            <div className="header-cell">Team member</div>
            <div className="header-cell">Amount</div>
            <div className="header-cell">Status</div>
            <div className="header-cell">Date</div>
                <div className="header-cell">Actions</div>
          </div>
          <div className="table-body">
                {revenueData.transactions && revenueData.transactions.length > 0 ? (
                  revenueData.transactions.map(transaction => (
                    <div key={transaction._id || transaction.id} className={`table-row ${transaction.isCOD ? 'cod-transaction' : ''} ${transaction.isPending ? 'pending-transaction' : ''}`}>
                      <div className="table-cell transaction-id">
                        <i className="fas fa-hashtag"></i>
                        <span>{transaction.transactionId || transaction._id?.slice(-8) || 'N/A'}</span>
                        {transaction.isCOD && (
                          <span className="cod-badge-small">
                            <i className="fas fa-money-bill-wave"></i> COD
                          </span>
                        )}
                        {transaction.isOrderSourced && (
                          <span className="order-badge-small">
                            <i className="fas fa-shopping-bag"></i> Order
                          </span>
                        )}
                      </div>
                      <div className="table-cell">
                        <span className={`type-badge type-${transaction.type}`}>
                          <i className={`fas fa-${transaction.type === 'income' || transaction.type === 'deposit' ? 'arrow-down' : 'arrow-up'}`}></i>
                          {transaction.type}
                        </span>
                      </div>
                      <div className="table-cell source-cell">
                        <i className={`fas fa-${transaction.isCOD ? 'money-bill-wave' : 'wallet'}`}></i>
                        <span>{transaction.source || 'N/A'}</span>
                        {transaction.orderNumber && (
                          <span className="order-ref">Order: {transaction.orderNumber}</span>
                        )}
                      </div>
                      <div className="table-cell team-member-cell">
                        <i className="fas fa-user-tag"></i>
                        <span>{transaction.teamMember || '—'}</span>
                      </div>
                      <div className="table-cell amount-cell">
                        <span className={`amount-value ${transaction.type === 'income' || transaction.type === 'deposit' ? 'positive' : 'negative'}`}>
                          {transaction.type === 'income' || transaction.type === 'deposit' ? '+' : '-'}
                          {formatCurrency(transaction.amount || 0)}
                        </span>
                      </div>
                <div className="table-cell">
                  <span 
                    className="status-badge" 
                    style={{ backgroundColor: getStatusColor(transaction.status) }}
                  >
                          <i className={`fas fa-${transaction.status === 'completed' ? 'check-circle' : transaction.status === 'pending' ? 'clock' : 'exclamation-circle'}`}></i>
                    {transaction.status}
                  </span>
                </div>
                      <div className="table-cell date-cell">
                        <i className="fas fa-calendar"></i>
                        <span>{new Date(transaction.transactionDate || transaction.createdAt).toLocaleDateString('en-PK', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}</span>
                      </div>
                      <div className="table-cell actions-cell">
                        <button 
                          className="action-btn view" 
                          title="View Details"
                          onClick={() => setViewTransactionModal({ isOpen: true, transaction })}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {/* Show edit/delete for manual transactions OR COD transactions */}
                        {!isCreatorScoped &&
                        canManageStoreRevenue &&
                        transaction.source === 'manual' && !transaction.isOrderSourced && (
                          <>
                            <button 
                              className="action-btn edit" 
                              title="Edit"
                              onClick={() => setEditTransactionModal({ isOpen: true, transaction })}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className="action-btn delete" 
                              title="Delete"
                              onClick={() => setDeleteTransactionConfirm({ 
                                isOpen: true, 
                                transactionId: transaction._id || transaction.id,
                                transactionInfo: `${transaction.type} - ${formatCurrency(transaction.amount || 0)}${transaction.isCOD ? ' (COD)' : ''}`
                              })}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="table-row empty-row">
                    <div className="empty-state-transactions">
                      <i className="fas fa-inbox"></i>
                      <p>No transactions found</p>
                      <button className="add-transaction-btn-empty" onClick={() => {/* TODO: Open add transaction modal */}}>
                        <i className="fas fa-plus"></i>
                        Add First Transaction
                      </button>
                    </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Accounts Tab */}
      {!isCreatorScoped && activeTab === 'accounts' && (
        <div className="accounts-section">
          <div className="section-header">
            <h2>Payment Accounts</h2>
            <button className="add-account-btn" onClick={() => setIsAddAccountModalOpen(true)}>
              <i className="fas fa-plus"></i> Add Account
            </button>
      </div>

          {successMessage && (
            <div className="success-message" style={{
              padding: '12px 20px',
              marginBottom: '20px',
              background: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-check-circle"></i> {successMessage}
            </div>
          )}
          
          {errorMessage && (
            <div className="error-message" style={{
              padding: '12px 20px',
              marginBottom: '20px',
              background: '#f8d7da',
              color: '#721c24',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-exclamation-circle"></i> {errorMessage}
            </div>
          )}
          <div className="accounts-grid">
            {revenueData.accounts && revenueData.accounts.length > 0 ? (
              revenueData.accounts.map(account => (
                <div key={account._id || account.id} className="account-card">
                  <div className="account-header">
                    <div className="account-icon">
                      <i className={`fas fa-${account.icon || 'wallet'}`}></i>
                    </div>
                    <div className="account-info">
                      <h3>{account.name}</h3>
                      <p className="account-method">{account.paymentMethod}</p>
                    </div>
                    <div className="account-status">
                      <span className={`status-badge ${account.isActive ? 'active' : 'inactive'}`}>
                        {account.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`visibility-badge ${account.isVisible ? 'visible' : 'hidden'}`}>
                        {account.isVisible ? 'Visible' : 'Hidden'}
                      </span>
                    </div>
                  </div>
                  {account.description && (
                    <p className="account-description">{account.description}</p>
                  )}
                  {(() => {
                    const det = getAccountDetailsObject(account);
                    const acctNum = pickVisibleAccountNumber(account, det);
                    const accTitle =
                      det.accountTitle ||
                      account.accountHolderName ||
                      account.accountTitle ||
                      det.accountHolderName;
                    const bankNm = det.bankName || account.bankName;
                    const proofWa =
                      det.paymentProofWhatsApp ||
                      det.payment_proof_whatsapp ||
                      account.paymentProofWhatsApp ||
                      account.payment_proof_whatsapp;
                    if (!acctNum && !accTitle && !bankNm && !proofWa) return null;
                    return (
                      <div
                        className="account-details"
                        style={{
                          padding: '15px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          marginTop: '15px',
                          fontSize: '14px',
                        }}
                      >
                        {acctNum ? (
                          <div className="account-detail-line account-detail-line--number">
                            <strong>Account Number:</strong>{' '}
                            <span className="account-detail-mono">{acctNum}</span>
                          </div>
                        ) : null}
                        {accTitle ? (
                          <div className="account-detail-line">
                            <strong>Account Title:</strong> {accTitle}
                          </div>
                        ) : null}
                        {bankNm ? (
                          <div className="account-detail-line">
                            <strong>Bank Name:</strong> {bankNm}
                          </div>
                        ) : null}
                        {proofWa ? (
                          <div className="account-detail-line account-detail-line--wa">
                            <strong>Payment proof WhatsApp:</strong> {proofWa}
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                  {account.stats && (
                    <div className="account-stats">
                      <div className="stat-item">
                        <span className="stat-label">Transactions:</span>
                        <span className="stat-value">{account.stats.totalTransactions || 0}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Total Amount:</span>
                        <span className="stat-value">{formatCurrency(account.stats.totalAmount || 0)}</span>
                      </div>
                    </div>
                  )}
                  <div className="account-actions">
                    <button 
                      className="action-btn edit" 
                      onClick={() => {
                        setEditingAccount(account);
                        setIsAddAccountModalOpen(true);
                      }}
                      title="Edit Account"
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button 
                      className="action-btn toggle-visibility" 
                      onClick={async () => {
                        try {
                          setErrorMessage('');
                          await toggleAccountVisibility(account._id || account.id, !account.isVisible);
                          setSuccessMessage(`Account ${account.isVisible ? 'hidden' : 'shown'} successfully!`);
                          setTimeout(() => setSuccessMessage(''), 3000);
                        } catch (error) {
                          setErrorMessage(error.message || 'Failed to toggle visibility');
                          setTimeout(() => setErrorMessage(''), 5000);
                        }
                      }}
                      title={account.isVisible ? 'Hide' : 'Show'}
                    >
                      <i className={`fas fa-eye${account.isVisible ? '' : '-slash'}`}></i>
                    </button>
                    <button 
                      className="action-btn toggle-status" 
                      onClick={async () => {
                        try {
                          setErrorMessage('');
                          await toggleAccountStatus(account._id || account.id, !account.isActive);
                          setSuccessMessage(`Account ${account.isActive ? 'deactivated' : 'activated'} successfully!`);
                          setTimeout(() => setSuccessMessage(''), 3000);
                        } catch (error) {
                          setErrorMessage(error.message || 'Failed to toggle status');
                          setTimeout(() => setErrorMessage(''), 5000);
                        }
                      }}
                      title={account.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <i className={`fas fa-${account.isActive ? 'check' : 'times'}`}></i>
                    </button>
                    <button 
                      className="action-btn delete" 
                      onClick={() => {
                        setDeleteConfirm({
                          isOpen: true,
                          accountId: account._id || account.id,
                          accountName: account.name
                        });
                      }}
                      title="Delete Account"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <i className="fas fa-wallet"></i>
                <p>No payment accounts found</p>
                <button className="add-account-btn" onClick={() => setIsAddAccountModalOpen(true)}>
                  <i className="fas fa-plus"></i> Add First Account
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {!isCreatorScoped && activeTab === 'promos' && (
        <div className="promos-tab-section">
          {successMessage && (
            <div
              className="success-message"
              style={{
                padding: '12px 20px',
                marginBottom: '20px',
                background: '#d4edda',
                color: '#155724',
                border: '1px solid #c3e6cb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <i className="fas fa-check-circle" /> {successMessage}
            </div>
          )}
          {errorMessage && (
            <div
              className="error-message"
              style={{
                padding: '12px 20px',
                marginBottom: '20px',
                background: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <i className="fas fa-exclamation-circle" /> {errorMessage}
            </div>
          )}
          <FreeDeliveryPromoSettings onNotify={handlePromoTabNotify} />
          <PromoCodesPanel onNotify={handlePromoTabNotify} />
        </div>
      )}

      {/* Add/Edit Account Modal */}
      <AddAccountModal
        isOpen={isAddAccountModalOpen}
        account={editingAccount}
        onClose={() => {
          setIsAddAccountModalOpen(false);
          setEditingAccount(null);
          setSuccessMessage('');
          setErrorMessage('');
        }}
        onSave={async (accountData) => {
          try {
            setErrorMessage('');
            if (editingAccount) {
              // Update existing account
              await updatePaymentAccount(editingAccount._id || editingAccount.id, accountData);
              setSuccessMessage('Payment account updated successfully!');
            } else {
              // Add new account
              await addPaymentAccount(accountData);
              setSuccessMessage('Payment account added successfully!');
            }
            setTimeout(() => {
              setSuccessMessage('');
            }, 3000);
            setIsAddAccountModalOpen(false);
            setEditingAccount(null);
            window.dispatchEvent(new CustomEvent('sheenwearPaymentAccountsUpdated'));
          } catch (error) {
            setErrorMessage(error.message || 'Failed to save account');
            setTimeout(() => setErrorMessage(''), 5000);
            throw error; // Let modal handle the error display
          }
        }}
      />

      {/* Deposit/Withdrawal Modal */}
      <DepositWithdrawalModal
        isOpen={depositWithdrawalModal.isOpen}
        onClose={() => setDepositWithdrawalModal({ isOpen: false, account: null })}
        account={depositWithdrawalModal.account}
        onSave={async (transactionData) => {
          try {
            await addRevenueTransaction(transactionData);
            
            // Refresh transactions first, then accounts (which calculate balance from transactions)
            await refetchTransactions();
            // Wait a bit to ensure transactions are loaded
            await new Promise(resolve => setTimeout(resolve, 200));
            // Then refresh all data which will recalculate balances from transactions
            await refetch();
            
            setSuccessMessage(`${transactionData.type === 'deposit' ? 'Deposit' : 'Withdrawal'} added successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);
          } catch (error) {
            setErrorMessage(error.message || 'Failed to add transaction');
            setTimeout(() => setErrorMessage(''), 3000);
            throw error;
          }
        }}
        loading={loading}
      />

      {/* Transaction Detail Modal */}
      <TransactionDetailModal
        isOpen={viewTransactionModal.isOpen}
        onClose={() => setViewTransactionModal({ isOpen: false, transaction: null })}
        transaction={viewTransactionModal.transaction}
      />

      {/* Edit Transaction Modal */}
      <DepositWithdrawalModal
        isOpen={editTransactionModal.isOpen}
        onClose={() => setEditTransactionModal({ isOpen: false, transaction: null })}
        account={editTransactionModal.transaction ? {
          name: editTransactionModal.transaction.accountName || (editTransactionModal.transaction.isCOD ? 'Cash on Delivery' : editTransactionModal.transaction.source),
          source: editTransactionModal.transaction.source,
          icon: editTransactionModal.transaction.isCOD ? 'money-bill-wave' : 'wallet',
          balance: editTransactionModal.transaction.amount || 0,
          id: editTransactionModal.transaction.accountId || (editTransactionModal.transaction.isCOD ? 'cod' : null),
          _id: editTransactionModal.transaction.accountId || (editTransactionModal.transaction.isCOD ? 'cod' : null),
          paymentMethod: editTransactionModal.transaction.isCOD ? 'cod' : editTransactionModal.transaction.source
        } : null}
        editingTransaction={editTransactionModal.transaction}
        onSave={async (transactionData) => {
          try {
            const transactionId = editTransactionModal.transaction._id || editTransactionModal.transaction.id;
            await updateRevenueTransaction(transactionId, transactionData);
            
            // Refresh transactions first, then accounts
            await refetchTransactions();
            await new Promise(resolve => setTimeout(resolve, 200));
            await refetch();
            
            setSuccessMessage('Transaction updated successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
            setEditTransactionModal({ isOpen: false, transaction: null });
          } catch (error) {
            setErrorMessage(error.message || 'Failed to update transaction');
            setTimeout(() => setErrorMessage(''), 3000);
            throw error;
          }
        }}
        loading={loading}
      />

      {/* Delete Transaction Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteTransactionConfirm.isOpen}
        onClose={() => {
          setDeleteTransactionConfirm({ isOpen: false, transactionId: null, transactionInfo: '' });
        }}
        onConfirm={async () => {
          try {
            setErrorMessage('');
            await deleteRevenueTransaction(deleteTransactionConfirm.transactionId);
            setSuccessMessage('Transaction deleted successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
            
            // Refresh data
            await refetchTransactions();
            await new Promise(resolve => setTimeout(resolve, 200));
            await refetch();
            
            setDeleteTransactionConfirm({ isOpen: false, transactionId: null, transactionInfo: '' });
          } catch (error) {
            setErrorMessage(error.message || 'Failed to delete transaction');
            setTimeout(() => setErrorMessage(''), 5000);
            setDeleteTransactionConfirm({ isOpen: false, transactionId: null, transactionInfo: '' });
          }
        }}
        title="Delete Transaction"
        message={`Are you sure you want to delete this transaction (${deleteTransactionConfirm.transactionInfo})? This action cannot be undone.`}
        type="danger"
      />

      {/* Delete Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => {
          setDeleteConfirm({ isOpen: false, accountId: null, accountName: '' });
        }}
        onConfirm={async () => {
          try {
            setErrorMessage('');
            await deletePaymentAccount(deleteConfirm.accountId);
            setSuccessMessage(`Account "${deleteConfirm.accountName}" deleted successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setDeleteConfirm({ isOpen: false, accountId: null, accountName: '' });
          } catch (error) {
            setErrorMessage(error.message || 'Failed to delete account');
            setTimeout(() => setErrorMessage(''), 5000);
            setDeleteConfirm({ isOpen: false, accountId: null, accountName: '' });
          }
        }}
        title="Delete Payment Account"
        message={`Are you sure you want to delete "${deleteConfirm.accountName}"? This action cannot be undone.`}
      />

    </div>
  );
};

export default Revenue;